import type { Commodity } from '@enmon/commons/lib/shared';
import { getManufacturerFamilyIds } from '@enmon/commons/lib/tools';
import { useMemo } from 'react';
import {
  createMeterModelCatalogItemFromBootstrapModel,
  type MeterModelCatalogItem,
} from '@/features/inventorization/meter-model-catalog-client';
import type { InventoryBuildingOption } from '@/features/inventorization/new-meter-location-client';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import type {
  LocationBootstrapBuilding,
  LocationBootstrapManufacturer,
  LocationBootstrapThirdParty,
} from '@/features/location-bootstrap/location-bootstrap-types';

export function useReviewFieldOptions(selectedManufacturer: string, selectedCommodity: Commodity | '') {
  const { bootstrap } = useLocationBootstrap();
  const buildingOptions = useMemo(
    () => deriveBuildingOptionsFromBootstrap(bootstrap?.buildings ?? []),
    [bootstrap?.buildings],
  );
  const meterModels = useMemo<MeterModelCatalogItem[]>(
    () => (bootstrap?.meterModels ?? []).map(createMeterModelCatalogItemFromBootstrapModel),
    [bootstrap?.meterModels],
  );
  const filteredMeterModels = useMemo(() => {
    return selectedCommodity ? meterModels.filter(model => model.commodity === selectedCommodity) : meterModels;
  }, [meterModels, selectedCommodity]);

  const manufacturerOptions = useMemo(() => {
    const options = new Map<string, { label: string; value: string }>();

    for (const model of filteredMeterModels) {
      if (model.manufacturerId && model.manufacturerName) {
        options.set(model.manufacturerId, { label: model.manufacturerName, value: model.manufacturerId });
      }
    }

    return [...options.values()].toSorted((left, right) => left.label.localeCompare(right.label));
  }, [filteredMeterModels]);

  const {
    meterModels: manufacturerMeterModels,
    modelFamilyManufacturers,
    modelOptions,
  } = useMemo(
    () => deriveFamilyModelOptions(bootstrap?.manufacturers ?? [], filteredMeterModels, selectedManufacturer),
    [bootstrap?.manufacturers, filteredMeterModels, selectedManufacturer],
  );
  const tenantOptions = useMemo(
    () => (bootstrap?.tenants ?? []).map(tenant => ({ label: tenant, value: tenant })),
    [bootstrap],
  );
  const ownerSuggestions = useMemo(
    () => (bootstrap?.ownerSuggestions ?? []).map(toOption),
    [bootstrap?.ownerSuggestions],
  );
  const utilityCompanyOwnerOptions = useMemo(
    () => deriveUtilityCompanyOwnerOptions(bootstrap?.thirdParties ?? [], selectedCommodity),
    [bootstrap?.thirdParties, selectedCommodity],
  );

  return {
    buildingOptions,
    manufacturerOptions,
    meterModels: manufacturerMeterModels,
    modelFamilyManufacturers,
    modelOptions,
    ownerSuggestions,
    tenantOptions,
    utilityCompanyOwnerOptions,
  };
}

// returns a list of options for the model family dropdown
export function deriveFamilyModelOptions(
  manufacturers: LocationBootstrapManufacturer[],
  models: MeterModelCatalogItem[],
  selectedManufacturer: string,
) {
  const normalizedManufacturer = selectedManufacturer.trim().toLowerCase();
  const picked = manufacturers.find(
    manufacturer =>
      String(manufacturer.id) === normalizedManufacturer || manufacturer.name.toLowerCase() === normalizedManufacturer,
  );
  const familyIds = picked ? getManufacturerFamilyIds(manufacturers, picked.id) : new Set<number>();
  const pickedId = picked ? String(picked.id) : undefined;
  const groupRank = (model: MeterModelCatalogItem) => (model.manufacturerId === pickedId ? 0 : 1);

  const meterModels = models
    .filter(model => !normalizedManufacturer || familyIds.has(Number(model.manufacturerId)))
    .toSorted(
      (left, right) =>
        groupRank(left) - groupRank(right) ||
        left.manufacturerName.localeCompare(right.manufacturerName) ||
        left.name.localeCompare(right.name),
    );

  const modelOptions = meterModels.map(model => ({
    group: model.manufacturerName,
    label: model.name,
    value: model.id,
  }));

  const groups = [...new Set(modelOptions.map(option => option.group))];

  return { meterModels, modelFamilyManufacturers: picked && groups.length > 1 ? groups : [], modelOptions };
}

function toOption(value: string) {
  return { label: value, value };
}

function deriveUtilityCompanyOwnerOptions(thirdParties: LocationBootstrapThirdParty[], commodity: Commodity | '') {
  return thirdParties
    .filter(thirdParty => thirdParty.commodities.some(com => commodity === com))
    .map(thirdParty => toOption(thirdParty.name))
    .toSorted((left, right) => left.label.localeCompare(right.label));
}

function deriveBuildingOptionsFromBootstrap(buildings: LocationBootstrapBuilding[]): InventoryBuildingOption[] {
  return buildings
    .map(building => ({
      code: building.code,
      id: String(building.id),
      name: building.name,
    }))
    .toSorted((left, right) => left.name.localeCompare(right.name));
}
